import React, {useCallback, useRef, useState} from "react";
import {fetchContacts, useGetContactsList} from "../hooks/useGetContacts";
import {fetchContactGroups} from "../hooks/useGetContactGroups";
import {Contact} from "../typedefs";
import {useSiren} from "../SirenContext";
import {useAuth} from "../../../context/user";
import {Action, ActionSelect} from "../../../components/ui/ActionSelect";
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID";
import {useArchiveToggleContact} from "../hooks/useArchiveToggleContact";
import {useDeleteContact} from "../hooks/useDeleteContact";
import {useDeleteAllArchivedContacts} from "../hooks/useDeleteAllArchivedContacts";
import {useCopyID} from "../../../lib/misc/useCopyID";
import {isNilObjectID} from "../../../lib/objectid/objectid";
import {useNavigate} from "react-router-dom";
import {PaginatedSelector, HeaderComponentProps} from "../../../components/paginated-selector/paginated-selector";
import {IconButton} from "../../../features/management-v2/import/inputs";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import {ContactTypeFilter} from "../components/ContactTypeFilter";
import {ConfirmationModal} from "../../../components/modals/ConfirmationModal";
import {SafelistCheckModal} from "../components/SafelistCheckModal";
import * as XLSX from "xlsx";

type ContactListProps = {
    archived?: boolean;
}

export function ContactList({archived = false}: ContactListProps) {
    const {clientID} = useSiren();
    const {isSuperAdmin} = useAuth();
    const {navigateWithClientID} = useNavigateWithClientID();
    const getItems = useGetContactsList(archived);
    const refetchRef = useRef<(() => void) | null>(null);

    const getColumns = useCallback((contact: Contact) => {
        const isUserLinked = !isNilObjectID(contact.userID);
        const s = contact.optInSummary;
        const optInCell = s
            ? <span className="text-sm font-mono" title={`${s.sent} sent, ${s.exempt} exempt, ${s.total} total active`}>{s.sent + s.exempt}/{s.total}</span>
            : <span className="text-sm text-light-grey">—</span>;
        return [
            isUserLinked ? <PersonIcon fontSize="small" titleAccess="User-linked"/> : <AlternateEmailIcon fontSize="small" titleAccess="Manual"/>,
            `${contact.firstName} ${contact.lastName}`,
            contact.phoneMobileWork?.e164 || "—",
            contact.phoneMobilePersonal?.e164 || "—",
            contact.emailWork || "—",
            contact.emailPersonal || "—",
            optInCell,
        ];
    }, []);

    const renderHeader = useCallback((props: HeaderComponentProps) => {
        if (props.columnIndex === 0) {
            return <ContactTypeFilter {...props}/>;
        }
        return <span className="font-bold">{props.label}</span>;
    }, []);

    const renderActions = useCallback((item: Contact) => (
        <ContactActions item={item} archived={archived} refetch={() => refetchRef.current?.()}/>
    ), [archived]);

    const [exporting, setExporting] = useState(false);
    const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
    const {submit: deleteAll, loading: deletingAll} = useDeleteAllArchivedContacts(clientID, () => {
        refetchRef.current?.();
    });

    const exportContacts = useCallback(async (search: string) => {
        setExporting(true);
        const filename = `contacts-${Math.floor(Date.now() / 1000)}.xlsx`;
        const workbook = XLSX.utils.book_new();
        try {
            // Build group ID → name map
            const groupMap = new Map<string, string>();
            let gp = 1;
            while (true) {
                const {results} = await fetchContactGroups(gp, 100, "", clientID, false);
                if (results.length === 0) break;
                results.forEach(g => groupMap.set(g.id, g.name));
                gp++;
            }

            // Fetch all contacts
            type ContactRow = { base: string[]; groups: string[] };
            const contactRows: ContactRow[] = [];
            let maxGroups = 0;
            let p = 1;
            while (true) {
                const {results} = await fetchContacts(p, 50, search, clientID, archived);
                if (results.length === 0) break;
                results.forEach((c) => {
                    const groupNames = (c.groupIDs ?? []).map(id => groupMap.get(id) ?? id);
                    if (groupNames.length > maxGroups) maxGroups = groupNames.length;
                    contactRows.push({
                        base: [
                            c.id, c.firstName, c.lastName, c.emailWork, c.emailPersonal,
                            c.phoneMobileWork?.e164 ?? "",
                            c.phoneMobilePersonal?.e164 ?? "",
                            c.phoneHome?.e164 ?? "",
                        ],
                        groups: groupNames,
                    });
                });
                p++;
            }

            const groupHeaders = Array(maxGroups).fill("Group");
            const header = ["ID", "First Name", "Last Name", "Email Address", "Personal Email Address", "Work Mobile Phone", "Personal Mobile Phone", "Home Phone", ...groupHeaders];
            const rows: string[][] = [header];
            contactRows.forEach(({base, groups}) => {
                const padded = [...groups];
                while (padded.length < maxGroups) padded.push("");
                rows.push([...base, ...padded]);
            });

            const worksheet = XLSX.utils.aoa_to_sheet(rows);
            // wch = width in characters
            const baseCols = [
                {wch: 28}, // ID
                {wch: 16}, // First Name
                {wch: 16}, // Last Name
                {wch: 28}, // Email Address
                {wch: 28}, // Personal Email Address
                {wch: 18}, // Work Mobile Phone
                {wch: 18}, // Personal Mobile Phone
                {wch: 18}, // Home Phone
            ];
            worksheet["!cols"] = [...baseCols, ...Array(maxGroups).fill({wch: 24})];
            XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts");
            XLSX.writeFile(workbook, filename);
        } catch (e) {
            console.error(e);
        } finally {
            setExporting(false);
        }
    }, [clientID, archived]);

    if (!clientID && isSuperAdmin) return null;

    return (
        <>
        <ConfirmationModal
            open={showDeleteAllModal}
            setOpen={setShowDeleteAllModal}
            title="Delete All Archived Contacts"
            message="All archived contacts (excluding user-linked contacts) will be permanently deleted. Their personal details will be removed from siren alert history after a grace period. This cannot be undone."
            confirmLabel="Delete All"
            confirmButtonColor="red"
            onConfirm={deleteAll}
        />
        <PaginatedSelector
            key={clientID}
            headers={["Type", "Name", "Work Mobile", "Personal Mobile", "Email", "Personal Email", "SMS Opt-ins"]}
            columnTemplate="140px minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 120px"
            getItems={getItems}
            getColumns={getColumns}
            disableSelect={true}
            renderActions={renderActions}
            renderHeader={renderHeader}
            nonClickableColumns={[0]}
            onColumnClick={(item) => {
                if (!isNilObjectID(item.userID)) {
                    window.open(`/management/users/edit/${item.userID}`, "_blank");
                } else {
                    navigateWithClientID(`siren/contacts/${item.id}/edit?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
                }
            }}
            syncWithUrl={true}
            onRefetchReady={(refetch) => { refetchRef.current = refetch }}
            renderBottomActions={(search) => (
                <div className="flex justify-end gap-4">
                    {archived && (
                        <div className="w-[200px]">
                            <IconButton text="Delete All" icon={<DeleteIcon/>} loading={deletingAll} onClick={() => setShowDeleteAllModal(true)}/>
                        </div>
                    )}
                    <div className="w-[200px]">
                        <IconButton text="Export" icon={<DownloadIcon/>} loading={exporting} onClick={() => exportContacts(search)}/>
                    </div>
                </div>
            )}
        />
        </>
    );
}

function ContactActions({item, archived, refetch}: { item: Contact; archived: boolean; refetch: () => void }) {
    const {clientID} = useSiren();
    const {isSuperAdmin} = useAuth();
    const {navigateWithClientID} = useNavigateWithClientID();
    const navigate = useNavigate();
    const {copyID, isCopied} = useCopyID();
    const {submit: toggleArchive} = useArchiveToggleContact(item.id, refetch);
    const {submit: deleteContact} = useDeleteContact(item.id, refetch);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSafelistModal, setShowSafelistModal] = useState(false);
    const isUserLinked = !isNilObjectID(item.userID);

    return (
        <>
            <ConfirmationModal
                open={showDeleteModal}
                setOpen={setShowDeleteModal}
                title="Delete Contact"
                message="This contact will be permanently deleted. Their personal details will be removed from all siren alert history after a grace period. This cannot be undone."
                confirmLabel="Delete"
                confirmButtonColor="red"
                onConfirm={deleteContact}
            />
            {isSuperAdmin && (
                <SafelistCheckModal
                    open={showSafelistModal}
                    setOpen={setShowSafelistModal}
                    contact={item}
                    clientID={clientID}
                />
            )}
            <ActionSelect label="Actions">
                {!archived && isUserLinked && (
                    <Action label="Edit User" onSelect={() => navigate(`/management/users/edit/${item.userID}`)}/>
                )}
                {!archived && !isUserLinked && (
                    <Action label="Edit" onSelect={() => navigateWithClientID(`siren/contacts/${item.id}/edit?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}/>
                )}
                {!isUserLinked && (
                    <Action label={archived ? "Restore" : "Archive"} onSelect={toggleArchive}/>
                )}
                {archived && !isUserLinked && (
                    <Action label="Delete" onSelect={() => setShowDeleteModal(true)}/>
                )}
                <Action label={isCopied(item.id) ? "Copied!" : "Copy ID"} onSelect={() => copyID(item.id)}/>
                {isSuperAdmin && (
                    <Action label="Safelist Check" onSelect={() => setShowSafelistModal(true)}/>
                )}
            </ActionSelect>
        </>
    );
}
